import {
  Input,
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import { MatCheckbox } from "@angular/material/checkbox";

import { AppStore } from "../../../../stores/app-store.service";
import { Subscription } from "rxjs";

import { FilterStatus } from "../../../../models/filters";
import { writeColorStatusSelection } from "../../../../models/color-status-filter";
import {
  IncidentAssociatedDeviceDTO,
  IncidentDTO
} from "../../../../models/incident";
import { SiteDTO } from "../../../../models/site";
import { IncidentControllerService } from "../../../../services/incident-controller.service";
import {
  filterTornOptions,
  filterUnknownOptions
} from "../../../../services/device-container-state-controller.service";
import { Range } from "../../../../models/range-status-days";
import { countActiveFilters } from "../../../../models/active-filters-count";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";
import { SearchbarListComponent } from "../../../elements/searchbar-list/search-site-list.component";
import { FilterControlComponent } from "../../../elements/filter-control/filter-control.component";
import { ButtonComponent } from "../../../elements/button/button.component";
import { IncidentTagComponent } from "../../incident/incident-tag/incident-tag.component";
import { TranslateModule } from "@ngx-translate/core";
import { FilterKey } from "../../../../services/filters.service";
interface DeviceDetail {
  containerType?: string;
  deviceId?: string;
  isIncident?: boolean;
}

@Component({
  selector: "app-header-list",
  templateUrl: "./header-list.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./header-list.component.scss"],
  imports: [
    MatTooltip,
    MatIcon,
    SearchbarListComponent,
    FilterControlComponent,
    ButtonComponent,
    IncidentTagComponent,
    TranslateModule
  ]
})
export class HeaderListComponent implements OnInit, OnChanges, OnDestroy {
  @Output() torn: EventEmitter<string> = new EventEmitter();
  @Output() unknown: EventEmitter<string> = new EventEmitter();
  @Output() showMap: EventEmitter<void> = new EventEmitter();
  @Output() showList: EventEmitter<void> = new EventEmitter();
  @Output() devices: EventEmitter<string[]> = new EventEmitter();
  @Output() incidents: EventEmitter<FilterKey[]> = new EventEmitter();
  @Output() lastSites: EventEmitter<string[]> = new EventEmitter();
  @Output() filter: EventEmitter<FilterStatus> = new EventEmitter();
  @Output() colorStatus: EventEmitter<FilterStatus[]> = new EventEmitter();
  @Output() containers: EventEmitter<string[]> = new EventEmitter();
  @Output() statusDays: EventEmitter<Range> = new EventEmitter();
  @Output() site: EventEmitter<string | undefined> = new EventEmitter();
  @Output() confidenceRadius: EventEmitter<string[]> = new EventEmitter();
  @Output() devicesTypes: EventEmitter<string[]> = new EventEmitter();
  @Output() addIncidentDevices: EventEmitter<number[]> = new EventEmitter();
  @Output() toggleFilters: EventEmitter<boolean> = new EventEmitter();

  @Input() listIncidentDevices: IncidentAssociatedDeviceDTO[];
  @Input() listIncidents: IncidentDTO[];
  @Input() listAddIncident: IncidentDTO[];
  @Input() inputData: DeviceDetail;
  @Input() listSites: SiteDTO[];
  @Input() loadingProgress: number;
  @Input() isEmptyIncidentDevice: boolean;

  @Input() filterTorn: string;
  @Input() filterUnknown: string;
  @Input() filterStatusDays: Range;
  @Input() filterDevices: string[] = [];
  @Input() filterLastSites: string[] = [];
  @Input() filterIncidents: FilterKey[] = [];
  @Input() filterContainers: string[] = [];
  @Input() filterConfidenceRadius: string[];
  @Input() filterDevicesTypes: string[];
  @Input() filterContainerCategory: string[] = [];
  @Input() filterSite: string | undefined = undefined;
  @Input() isSidenavOpen: boolean = false;

  public index: number = 20;
  public isLoading: boolean = false;
  public searchFocus: boolean = false;
  public activeSite: SiteDTO = undefined;
  public activeRadius: number = undefined;
  public statusList: FilterStatus[] = [];

  public tornFilterOptions: string[] = Object.keys(filterTornOptions);
  public unknownFilterOptions: string[] = Object.keys(filterUnknownOptions);

  private subscription: Subscription = new Subscription();

  @ViewChild("selectionCheckbox", { static: true })
  public selectionCheckbox: MatCheckbox;

  constructor(
    private store: AppStore,
    private incidentService: IncidentControllerService
  ) {}

  public ngOnInit() {
    this.initStore();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.listSites) {
      if (!this.activeSite && this.filterSite) {
        this.activeSite = this.listSites.find(
          (site) => site.code === this.filterSite
        );
      }
    }
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private initStore(): void {
    this.subscription.add(
      this.store.statusStore$.subscribe((statusData: Array<FilterStatus>) => {
        this.statusList = statusData;
        writeColorStatusSelection(statusData);
      })
    );
  }

  /**
   * The picked site, emitted for the page to navigate to. Nothing is written to
   * localStorage: the open site is the `/list/site/:site_code` segment, and the URL
   * is the only place it is remembered.
   */
  public siteChanged(): void {
    this.activeSite = this.listSites.find(
      (site) => site.code === this.filterSite
    );
    if (this.activeSite) {
      this.site.emit(this.activeSite.code);
    }
  }

  public resetSite(): void {
    this.activeSite = undefined;
    this.site.emit(undefined);
  }

  public manageFilterSite(siteCode: string): void {
    this.filterSite = siteCode;

    if (!siteCode) {
      this.resetSite();
    } else {
      this.siteChanged();
    }
  }

  public manageContainers(containers: string[]): void {
    this.containers.emit(containers);
  }

  public manageDevice(devices: string[]): void {
    this.devices.emit(devices);
  }

  public manageStatusDays(statusDays: Range): void {
    this.statusDays.emit(statusDays);
  }

  public manageConfidenceRadius(radius: string[]): void {
    this.confidenceRadius.emit(radius);
  }

  public manageDevicesTypes(devicesTypes: string[]): void {
    this.devicesTypes.emit(devicesTypes);
  }

  public manageLAS(sites: string[]): void {
    this.lastSites.emit(sites);
  }

  public manageFilters(filter): void {
    this.filter.emit(filter);
  }

  public manageColorStatus(colors: FilterStatus[]): void {
    this.colorStatus.emit(colors);
  }

  public manageIncidents(incidents: FilterKey[]): void {
    this.incidents.emit(incidents);
  }

  public manageTorn(torn: string): void {
    this.torn.emit(torn);
  }

  public manageUnknown(unknown: string): void {
    this.unknown.emit(unknown);
  }

  public addIncident(incidentsIds: FilterKey[]): void {
    if (!incidentsIds?.length) {
      this.addIncidentDevices.emit();
    } else {
      this.isLoading = true;

      this.subscription.add(
        this.incidentService
          .getAllIncidentsByIncidentID(incidentsIds)
          .subscribe((incidents: IncidentDTO[]) => {
            let devices: Partial<IncidentAssociatedDeviceDTO>[] = [];
            incidents.forEach((incident) => {
              this.listIncidentDevices.forEach((device) => {
                devices.push({
                  isOpened: false,
                  incidentId: incident.id,
                  deviceId: device.deviceId,
                  lastAuthorizedSite: incident.lastAuthorizedSite,
                  containerType: incident.containerType,
                  assignedBy: incident.createdBy,
                  closingDate: null
                });
              });
            });
            this.incidentService
              .postSelectDevices(devices)
              .subscribe((_res) => {
                incidents.forEach((incident) => {
                  if (!incident.currentSite) incident.currentSite = undefined;
                  this.incidentService
                    .getCountDevices(incident.id)
                    .subscribe((res) => {
                      incident.totalQuantity = res.count;
                      this.subscription.add(
                        this.incidentService.patchIncident(incident).subscribe()
                      );
                    });
                });
                this.isLoading = false;
              });
          })
      );
    }
  }

  public backTolist(): void {
    this.showList.emit();
  }

  public backToMap(): void {
    this.showMap.emit();
  }

  public onToggleFilters(): void {
    this.toggleFilters.emit(!this.isSidenavOpen);
  }

  public get activeFiltersCount(): number {
    return countActiveFilters({
      containers: this.filterContainers,
      containerCategory: this.filterContainerCategory,
      incidents: this.filterIncidents,
      lastSites: this.filterLastSites,
      devices: this.filterDevices,
      confidenceRadius: this.filterConfidenceRadius,
      devicesTypes: this.filterDevicesTypes,
      statusDays: this.filterStatusDays,
      colorStatus: this.statusList
    });
  }
}
